/**
 * SyncStatus Component
 * 
 * This component displays GMB sync status and history, including:
 * - Current sync status with visual indicators
 * - Sync history and logs
 * - Manual sync triggers
 * - Error handling and troubleshooting
 */

import React, { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { 
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertCircle,
  Clock,
  Activity,
  Download,
  FileText,
  Loader2,
  RotateCw
} from 'lucide-react'
import type { GmbSyncLog, GmbSyncStatus } from '@/features/gmb/types'

export type GmbSyncType = 'accounts' | 'locations' | 'reviews' | 'posts' | 'questions' | 'insights' | 'all'

/**
 * Props interface for SyncStatus component
 */
export interface SyncStatusProps {
  /** Current sync logs */
  syncLogs: GmbSyncLog[]
  /** Whether a sync is currently in progress */
  isSyncing?: boolean
  /** Current sync progress (0-100) */
  syncProgress?: number
  /** Callback to trigger manual sync */
  onSync?: (syncType: GmbSyncType) => Promise<void>
  /** Show detailed logs */
  showDetailedLogs?: boolean
  /** Enable auto-refresh */
  autoRefresh?: boolean
}

/**
 * Get status color and icon
 */
function getStatusDisplay(status: GmbSyncStatus) {
  switch (status) {
    case 'success':
      return {
        color: 'bg-chart-5/10 text-chart-5',
        icon: <CheckCircle className="h-4 w-4" />,
        label: 'Success'
      }
    case 'partial':
      return {
        color: 'bg-accent/10 text-accent',
        icon: <AlertCircle className="h-4 w-4" />,
        label: 'Partial Success'
      }
    case 'failed':
      return {
        color: 'bg-destructive/10 text-destructive',
        icon: <XCircle className="h-4 w-4" />,
        label: 'Failed'
      }
    case 'in_progress':
      return {
        color: 'bg-chart-4/10 text-chart-4',
        icon: <Loader2 className="h-4 w-4 animate-spin" />,
        label: 'In Progress'
      }
    default:
      return {
        color: 'bg-muted text-muted-foreground',
        icon: <Clock className="h-4 w-4" />,
        label: 'Unknown'
      }
  }
}

/**
 * Get sync type display name
 */
function getSyncTypeDisplay(type: GmbSyncType): string {
  const displays: Record<GmbSyncType, string> = {
    accounts: 'Accounts',
    locations: 'Locations',
    reviews: 'Reviews',
    posts: 'Posts',
    questions: 'Questions',
    insights: 'Insights',
    all: 'Full Sync'
  }
  return displays[type] || type
}

/**
 * Enhanced sync log item component with retry functionality
 */
function SyncLogItem({ 
  log, 
  enableRetry, 
  onRetryFailedItems 
}: { 
  log: GmbSyncLog
  enableRetry?: boolean
  onRetryFailedItems?: (syncType: GmbSyncType, failedItemIds: string[]) => Promise<void>
}) {
  const [expanded, setExpanded] = useState(false)
  const [isRetrying, setIsRetrying] = useState(false)
  const status = getStatusDisplay(log.status)
  
  // Calculate duration if completed
  const duration = log.completed_at 
    ? new Date(log.completed_at).getTime() - new Date(log.started_at).getTime()
    : null

  const successRate = log.items_synced + log.items_failed > 0
    ? Math.round((log.items_synced / (log.items_synced + log.items_failed)) * 100)
    : 0

  const handleRetry = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!onRetryFailedItems || !log.sync_type) return
    
    setIsRetrying(true)
    try {
      // Extract failed item IDs from error message if available
      const failedIds: string[] = [] // Would need to parse from error_message
      await onRetryFailedItems(log.sync_type as GmbSyncType, failedIds)
    } finally {
      setIsRetrying(false)
    }
  }

  return (
    <div className="border rounded-lg p-3 hover:shadow-sm transition-shadow">
      <div 
        className="flex items-center justify-between cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <Badge className={status.color}>
            {status.icon}
            <span className="ml-1">{status.label}</span>
          </Badge>
          
          <div className="space-y-0.5">
            <p className="text-sm font-medium">
              {getSyncTypeDisplay(log.sync_type)}
            </p>
            <p className="text-xs text-muted-foreground">
              {new Date(log.started_at).toLocaleString()}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-4 text-sm">
          <div className="text-right">
            <div className="flex items-center gap-2">
              <Download className="h-3 w-3 text-green-600" />
              <span>{log.items_synced}</span>
              {log.items_failed > 0 && (
                <>
                  <XCircle className="h-3 w-3 text-red-600 ml-2" />
                  <span className="text-red-600">{log.items_failed}</span>
                </>
              )}
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              {duration && (
                <span>{Math.round(duration / 1000)}s</span>
              )}
              <span>•</span>
              <span>{successRate}% success</span>
            </div>
          </div>
          
          {enableRetry && log.items_failed > 0 && log.status !== 'in_progress' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRetry}
              disabled={isRetrying}
              className="ml-2"
            >
              {isRetrying ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <RotateCw className="h-3 w-3" />
              )}
            </Button>
          )}
        </div>
      </div>

      {expanded && (
        <div className="mt-3 pt-3 border-t space-y-3">
          <div className="grid grid-cols-2 gap-3 text-xs">
            {log.gmb_account_id && (
              <div>
                <span className="text-muted-foreground">Account:</span>{' '}
                <span className="font-mono">{log.gmb_account_id}</span>
              </div>
            )}
            
            {log.gmb_location_id && (
              <div>
                <span className="text-muted-foreground">Location:</span>{' '}
                <span className="font-mono">{log.gmb_location_id}</span>
              </div>
            )}
            
            {log.initiated_by && (
              <div>
                <span className="text-muted-foreground">Initiated by:</span>{' '}
                {log.initiated_by}
              </div>
            )}
            
            {duration && (
              <div>
                <span className="text-muted-foreground">Duration:</span>{' '}
                {formatDuration(duration)}
              </div>
            )}
          </div>
          
          {/* Performance Metrics */}
          {(log.items_synced > 0 || log.items_failed > 0) && (
            <div className="bg-muted rounded-lg p-3 space-y-2">
              <p className="text-xs font-medium">Sync Performance</p>
              <div className="space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Items/second:</span>
                  <span className="font-medium">
                    {duration ? Math.round((log.items_synced + log.items_failed) / (duration / 1000) * 10) / 10 : 0}
                  </span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Success rate:</span>
                  <span className="font-medium">{successRate}%</span>
                </div>
              </div>
            </div>
          )}
          
          {log.error_message && (
            <Alert variant="destructive">
              <AlertCircle className="h-3 w-3" />
              <AlertTitle className="text-xs">Error Details</AlertTitle>
              <AlertDescription className="text-xs mt-1">
                {log.error_message}
              </AlertDescription>
            </Alert>
          )}
        </div>
      )}
    </div>
  )
}

// Helper function to format duration
function formatDuration(ms: number): string {
  const seconds = Math.floor(ms / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  
  if (hours > 0) {
    return `${hours}h ${minutes % 60}m`
  } else if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`
  } else {
    return `${seconds}s`
  }
}

/**
 * Enhanced props interface for SyncStatus component
 */
export interface ExtendedSyncStatusProps extends SyncStatusProps {
  /** Current sync details */
  currentSyncDetails?: {
    itemsProcessed: number
    totalItems: number
    currentLocation?: string
    estimatedTimeRemaining?: number
  }
  /** Show sync queue */
  syncQueue?: GmbSyncType[]
  /** Enable retry for failed items */
  enableRetry?: boolean
  /** Callback for retrying failed sync */
  onRetryFailedItems?: (syncType: GmbSyncType, failedItemIds: string[]) => Promise<void>
}

/**
 * Component for displaying GMB sync status
 */
export function SyncStatus({
  syncLogs,
  isSyncing = false,
  syncProgress = 0,
  onSync,
  showDetailedLogs = true,
  autoRefresh = false,
  currentSyncDetails,
  syncQueue = [],
  enableRetry = false,
  onRetryFailedItems
}: ExtendedSyncStatusProps) {
  const [selectedSyncType, setSelectedSyncType] = useState<GmbSyncType>('all')
  const [showFailedOnly] = useState(false)

  // Get latest sync info
  const latestSync = syncLogs[0]
  const lastSuccessfulSync = syncLogs.find(log => log.status === 'success')

  // Calculate stats
  const stats = {
    totalSyncs: syncLogs.length,
    successfulSyncs: syncLogs.filter(log => log.status === 'success').length,
    failedSyncs: syncLogs.filter(log => log.status === 'failed').length,
    partialSyncs: syncLogs.filter(log => log.status === 'partial').length,
    averageDuration: syncLogs
      .filter(log => log.completed_at)
      .reduce((acc, log) => {
        const duration = new Date(log.completed_at!).getTime() - new Date(log.started_at).getTime()
        return acc + duration
      }, 0) / syncLogs.filter(log => log.completed_at).length || 0,
    totalItemsSynced: syncLogs.reduce((sum, log) => sum + log.items_synced, 0),
    totalItemsFailed: syncLogs.reduce((sum, log) => sum + log.items_failed, 0)
  }

  // Calculate sync performance metrics
  const calculateSyncSpeed = () => {
    if (!currentSyncDetails || !isSyncing) return null
    
    const itemsPerSecond = currentSyncDetails.itemsProcessed / (syncProgress / 100 * stats.averageDuration / 1000)
    return Math.round(itemsPerSecond * 10) / 10
  }

  // Format time remaining
  const formatTimeRemaining = (seconds: number) => {
    if (seconds < 60) return `${Math.round(seconds)}s`
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`
    return `${Math.round(seconds / 3600)}h ${Math.round((seconds % 3600) / 60)}m`
  }

  // Handle manual sync
  const handleSync = async () => {
    if (onSync) {
      await onSync(selectedSyncType)
    }
  }

  return (
    <div className="space-y-4">
      {/* Current Status Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Sync Status</CardTitle>
              <CardDescription>
                Google My Business data synchronization
              </CardDescription>
            </div>
            <Badge variant="outline">
              <Activity className="h-3 w-3 mr-1" />
              {autoRefresh ? 'Auto-refresh ON' : 'Manual refresh'}
            </Badge>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Current Sync Progress */}
          {isSyncing && (
            <div className="space-y-3">
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Syncing {getSyncTypeDisplay(selectedSyncType)}...
                  </span>
                  <span className="font-medium">{syncProgress}%</span>
                </div>
                <Progress value={syncProgress} className="h-2" />
              </div>
              
              {/* Detailed Progress Info */}
              {currentSyncDetails && (
                <div className="grid grid-cols-2 gap-4 text-xs">
                  <div>
                    <p className="text-muted-foreground">Items processed</p>
                    <p className="font-medium">
                      {currentSyncDetails.itemsProcessed} / {currentSyncDetails.totalItems || '?'}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Speed</p>
                    <p className="font-medium">
                      {calculateSyncSpeed() || '?'} items/sec
                    </p>
                  </div>
                  {currentSyncDetails.currentLocation && (
                    <div className="col-span-2">
                      <p className="text-muted-foreground">Current location</p>
                      <p className="font-medium truncate">{currentSyncDetails.currentLocation}</p>
                    </div>
                  )}
                  {currentSyncDetails.estimatedTimeRemaining && (
                    <div className="col-span-2">
                      <p className="text-muted-foreground">Time remaining</p>
                      <p className="font-medium">
                        {formatTimeRemaining(currentSyncDetails.estimatedTimeRemaining)}
                      </p>
                    </div>
                  )}
                </div>
              )}
              
              {/* Sync Queue */}
              {syncQueue.length > 0 && (
                <div className="pt-2 border-t">
                  <p className="text-xs text-muted-foreground mb-1">Sync queue:</p>
                  <div className="flex flex-wrap gap-1">
                    {syncQueue.map((type, index) => (
                      <Badge key={type} variant="outline" className="text-xs">
                        {index + 1}. {getSyncTypeDisplay(type)}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Latest Sync Info */}
          {latestSync && !isSyncing && (
            <Alert>
              <Clock className="h-4 w-4" />
              <AlertTitle className="text-sm">Last Sync</AlertTitle>
              <AlertDescription className="text-xs">
                {getSyncTypeDisplay(latestSync.sync_type)} completed{' '}
                {new Date(latestSync.started_at).toLocaleString()}
                {latestSync.status === 'success' && (
                  <span className="text-green-600 ml-1">
                    • {latestSync.items_synced} items synced
                  </span>
                )}
              </AlertDescription>
            </Alert>
          )}

          {/* Sync Stats */}
          <div className="grid grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold">{stats.totalSyncs}</div>
              <p className="text-xs text-muted-foreground">Total Syncs</p>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {stats.successfulSyncs}
              </div>
              <p className="text-xs text-muted-foreground">Successful</p>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">
                {stats.failedSyncs}
              </div>
              <p className="text-xs text-muted-foreground">Failed</p>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold">
                {Math.round(stats.averageDuration / 1000)}s
              </div>
              <p className="text-xs text-muted-foreground">Avg Duration</p>
            </div>
          </div>

          {/* Manual Sync Controls */}
          {onSync && (
            <div className="pt-4 border-t">
              <div className="flex items-center gap-2">
                <select
                  value={selectedSyncType}
                  onChange={(e) => setSelectedSyncType(e.target.value as GmbSyncType)}
                  className="flex-1 px-3 py-2 border rounded-md text-sm"
                  disabled={isSyncing}
                >
                  <option value="all">Full Sync (All Data)</option>
                  <option value="accounts">Accounts Only</option>
                  <option value="locations">Locations Only</option>
                  <option value="reviews">Reviews Only</option>
                  <option value="posts">Posts Only</option>
                  <option value="questions">Questions Only</option>
                </select>
                
                <Button
                  onClick={handleSync}
                  disabled={isSyncing}
                  size="sm"
                >
                  <RefreshCw className={`h-4 w-4 mr-1 ${isSyncing ? 'animate-spin' : ''}`} />
                  {isSyncing ? 'Syncing...' : 'Sync Now'}
                </Button>
              </div>
              
              <p className="text-xs text-muted-foreground mt-2">
                Last successful sync:{' '}
                {lastSuccessfulSync
                  ? new Date(lastSuccessfulSync.started_at).toLocaleString()
                  : 'Never'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sync History */}
      {showDetailedLogs && syncLogs.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Sync History</CardTitle>
              <Button variant="ghost" size="sm">
                <FileText className="h-4 w-4 mr-1" />
                Export Logs
              </Button>
            </div>
          </CardHeader>
          
          <CardContent>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {syncLogs
                .filter(log => !showFailedOnly || log.status === 'failed' || log.status === 'partial')
                .map((log) => (
                  <SyncLogItem 
                    key={log.id} 
                    log={log}
                    enableRetry={enableRetry}
                    onRetryFailedItems={onRetryFailedItems}
                  />
                ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

export default SyncStatus
